import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';

import {
  CheckCircleOutlined,
  HistoryOutlined,
  QuestionCircleOutlined,
  SwapOutlined,
  WarningFilled,
} from '@ant-design/icons';

import { Alert, Modal, Radio } from 'antd';
import CalendarWeekBar from 'components/cards/CalendarWeek';
import HistoryModule from 'components/cards/HistoryModule';
import ModuleCard from 'components/cards/ModuleCard';
import NextPeriodPrediction from 'components/cards/NextPeriodPrediction';
import CompareCycles from 'components/charts/CompareCycles';
import { CycleDotGraphExample } from 'components/charts/CycleDotGraph';
import PastCyclesLength from 'components/charts/PastCyclesLength';
import HorizontalTrackingCards from 'components/HorizontalTrackingCards';
import PeriodQuickFillModal from 'components/modals/PeriodQuickFillModal';
import PageTopBar from 'components/navigation/PageTopBar';
import SymptomTrackingReminder from 'components/SymptomTrackingReminder';
import { Button } from 'components/ui';
import dayjs from 'dayjs';
import { colors, fontSize, palette, shadows } from 'design';
import useCheckNotificationsPermissions from 'hooks/useCheckNotificationsPermissions';
import usePreferences from 'hooks/usePreferences';
import useReminderState from 'hooks/useReminderState';
import useSchedulePeriodNotifications from 'hooks/useSchedulePeriodNotifications';
import useShouldAskForPeriod from 'hooks/useShouldAskForPeriod';
import useSymptomTrackingData from 'hooks/useSymptomTrackingData';
import posthog from 'posthog-js';
import Keys from 'preferences/preferences';
import { usePushNotificationOptIn } from 'push/PushNotificationOptInContext';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import Platform from 'utils/Platform';
import ScrollUtil from 'utils/ScrollUtil';
import SymptomTrackingSurveyModal from './SymptomTrackingSurvey';

const useStyles = createUseStyles({
  pageContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    overflow: 'hidden',
    position: 'relative',
    backgroundColor: colors.surface.page,
  },
  container: {
    padding: '8px 12px 32px',
    overflowY: 'scroll',
    flexGrow: 1,
    maxWidth: 600,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 32,
  },
  headerContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    gap: 24,
    position: 'relative',
  },
  errorFallback: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    textAlign: 'center',
    color: colors.dataViz.bad,
    padding: '0px 32px',
  },
  standaloneReminderContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
  reminderContainer: {
    position: 'absolute',
    bottom: 0,
    right: 0,
  },
  periodReminderSuggestionContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: 12,
  },
  helperIcon: {
    cursor: 'pointer',
    color: colors.text.disabled,
    marginTop: 8,
  },
  graphSwitcher: {
    marginTop: 24,
    '& .ant-radio-group': {
      borderRadius: 8,
      boxShadow: shadows.xs,
    },
    '& .ant-radio-button-wrapper': {
      border: 'none',
      borderInlineStart: 'none',
      fontSize: fontSize.sm,
      // The wrapper lays out the (hidden) radio dot and the label as inline
      // content, so the icon+text pair sits on the surrounding text baseline
      // rather than truly centred — flexing the wrapper itself, not just the
      // label span, is what actually centres it.
      display: 'inline-flex',
      alignItems: 'center',
    },
    '& .ant-radio-button-wrapper::before': {
      display: 'none',
    },
    '& .ant-radio-button-label': {
      display: 'inline-flex',
      alignItems: 'center',
    },
    // Scoped to this switcher rather than the global `Radio` theme tokens,
    // which set the checked fill to `accentSubtle` for every radio button in
    // the app — this one asks specifically for paon.
    //
    // `!important`: antd's own theme-generated class carries the same
    // specificity (one class each) and wins the cascade tie otherwise.
    '& .ant-radio-button-wrapper-checked': {
      backgroundColor: `${colors.brand.accent} !important`,
      color: `${colors.text.inverse} !important`,
    },
  },
  noPeriodsContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,

    '& button': {
      padding: 0,
    },

    '& p': {
      margin: 0,
    },
  },
  emptySpaceBehindSurveyButton: {
    height: 64,
    minHeight: 64,
  },
  fillSurveyButton: {
    position: 'absolute',
    bottom: 0,
    width: '100%',
    padding: 32,
    background: `linear-gradient(to top, ${colors.surface.page}, ${colors.surface.page} 50%, ${colors.surface.page}00 100%);`,
    zIndex: 10,
  },
  cycleDotGraphExample: {
    height: 8,
    width: 8,
    borderRadius: '50%',
    display: 'inline-block',
    margin: '0px 4px',
    boxSizing: 'border-box',
  },
});

export default function SymptomTracking() {
  // Hooks
  const styles = useStyles();
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const { doesUserHavePushNotifications } = useCheckNotificationsPermissions();

  // period tracking preferences
  const { isPeriodTrackingEnabled, setShouldAskForPeriod } = useShouldAskForPeriod();

  // reminder state (managed via both preferences and Redux store)
  const { isPeriodReminderEnabled, areDailyNotificationsEnabled, setPeriodReminderEnabled } = useReminderState();

  const isAnyNotificationEnabled = areDailyNotificationsEnabled || isPeriodReminderEnabled;
  const { schedulePeriodReminder } = useSchedulePeriodNotifications();

  const {
    set: setDidWeAlreadySuggestPeriodReminder,
    value: didWeAlreadySuggestPeriodReminder,
    loading: loadingDidWeAlreadySuggestPeriodReminder,
  } = usePreferences(Keys.TRACKING_DID_WE_ALREADY_SUGGEST_PERIOD_REMINDER);

  const { requestOptIn, enableNotifications } = usePushNotificationOptIn();

  const areNotificationsBlocked = isAnyNotificationEnabled && !doesUserHavePushNotifications;

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [showPeriodReminderSuggestion, setShowPeriodReminderSuggestion] = useState(false);

  const [isSurveyModalOpen, setIsSurveyModalOpen] = useState(false);

  // Helper modal states
  const [showCycleLengthHelp, setShowCycleLengthHelp] = useState(false);
  const [showCompareCyclesHelp, setShowCompareCyclesHelp] = useState(false);

  // period quick fill modal state
  const [isQuickFillModalOpen, setIsQuickFillModalOpen] = useState(false);

  // history / compare toggle state
  const [selectedTab, setSelectedTab] = useState<'history' | 'compare'>('history');

  const shouldShowPeriodReminderSuggestion =
    Platform.isNative() &&
    ((showPeriodReminderSuggestion &&
      !loadingDidWeAlreadySuggestPeriodReminder &&
      !(didWeAlreadySuggestPeriodReminder === true)) ||
      areNotificationsBlocked);

  const [periodReminderPage, setPeriodReminderPage] = useState<
    'ask' | 'confirmNo' | 'confirmYes' | 'notificationsDisabled'
  >('ask');

  const { refresh, hasTrackingBeenUsed, probableNextPeriodDates, hasTrackingForToday } = useSymptomTrackingData();

  const enoughDataForHistory = hasTrackingBeenUsed;

  useEffect(() => {
    // sometimes in functional tests, I think useRequest considers two very close requests as the same
    // and does not refresh the data when this page mounts. This fixes it
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (areNotificationsBlocked) {
      setPeriodReminderPage('notificationsDisabled');
    }
  }, [areNotificationsBlocked]);

  useEffect(() => {
    if (isPeriodTrackingEnabled && probableNextPeriodDates && !showPeriodReminderSuggestion) {
      setShowPeriodReminderSuggestion(true);
      setPeriodReminderPage('ask');
    }
  }, [isPeriodTrackingEnabled, probableNextPeriodDates, showPeriodReminderSuggestion]);

  const onFillCycles = () => {
    // open new quick fill modal with infinite calendar
    setIsQuickFillModalOpen(true);
    posthog.capture('tracking.period_quickfill_started');
  };

  const wasQuickFillOpenRef = useRef(false);
  useEffect(() => {
    if (wasQuickFillOpenRef.current && !isQuickFillModalOpen) {
      requestOptIn('first_tracking');
    }
    wasQuickFillOpenRef.current = isQuickFillModalOpen;
  }, [isQuickFillModalOpen, requestOptIn]);

  const onValidateFillingFirstPeriods = () => {
    setIsCalendarOpen(false);
    ScrollUtil.scrollToTop(containerRef.current);
    posthog.capture('tracking.period_quickfill_validated');
  };

  const enablePeriodReminder = useCallback(() => {
    setPeriodReminderEnabled(true);
    setPeriodReminderPage('confirmYes');
    if (probableNextPeriodDates) {
      schedulePeriodReminder(probableNextPeriodDates.start, areDailyNotificationsEnabled);
    }
    posthog.capture('tracking.period_reminder.enabled', { source: 'suggester' });
  }, [setPeriodReminderEnabled, probableNextPeriodDates, schedulePeriodReminder, areDailyNotificationsEnabled]);

  const refusePeriodReminder = () => {
    setPeriodReminderPage('confirmNo');
    posthog.capture('tracking.period_reminder.refused');
  };

  useEffect(() => {
    // when coming back from notification settings, if notifications have been enabled,
    // enable the reminder and switch to the confirmation page
    if (doesUserHavePushNotifications && periodReminderPage === 'notificationsDisabled') {
      enablePeriodReminder();
    }
  }, [doesUserHavePushNotifications, periodReminderPage, enablePeriodReminder]);

  const setIsCalendarOpenWithScrollHandler = (newIsCalendarOpen: boolean) => {
    setIsCalendarOpen(newIsCalendarOpen);
    if (newIsCalendarOpen) {
      containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const ErrorFallback = (props: FallbackProps) => {
    void props;
    return (
      <div className={styles.errorFallback}>
        <p>{t('symptomTracking.error.title')}</p>
      </div>
    );
  };

  return (
    <div className={styles.pageContainer}>
      <PageTopBar title={t('symptomTracking.title')} />
      <div className={styles.container} ref={containerRef}>
        <CalendarWeekBar
          isCalendarOpen={isCalendarOpen}
          setIsCalendarOpen={setIsCalendarOpenWithScrollHandler}
          onFillCycles={onFillCycles}
        />
        {/* Quick fill modal for cycles */}
        <PeriodQuickFillModal
          open={isQuickFillModalOpen}
          onClose={() => setIsQuickFillModalOpen(false)}
          onValidate={() => {
            setIsQuickFillModalOpen(false);
            onValidateFillingFirstPeriods();
          }}
        />

        {/* Next period prediction and reminder */}
        {isPeriodTrackingEnabled && (
          <div className={styles.headerContainer}>
            <NextPeriodPrediction />
            <div className={styles.reminderContainer}>
              <SymptomTrackingReminder mode="iconOnly" />
            </div>
          </div>
        )}

        {/* Standalone reminder when period tracking is disabled */}
        {!isPeriodTrackingEnabled && (
          <div className={styles.standaloneReminderContainer}>
            <SymptomTrackingReminder mode="full" />
          </div>
        )}

        {/* Period reminder suggestion */}
        {shouldShowPeriodReminderSuggestion && (
          <Alert
            type={periodReminderPage === 'notificationsDisabled' ? 'warning' : 'success'}
            style={{ width: '100%' }}
            message={
              <div className={styles.periodReminderSuggestionContainer}>
                {periodReminderPage === 'ask' && (
                  <>
                    <p>{t('symptomTracking.periodReminder.question')}</p>
                    <div>
                      <Button variant="ghost" onClick={refusePeriodReminder}>
                        {t('symptomTracking.periodReminder.later')}
                      </Button>
                      <Button
                        variant="primary"
                        onClick={() => {
                          if (doesUserHavePushNotifications) {
                            enablePeriodReminder();
                          } else {
                            setPeriodReminderPage('notificationsDisabled');
                          }
                        }}
                      >
                        {t('symptomTracking.periodReminder.yes')}
                      </Button>
                    </div>
                  </>
                )}
                {periodReminderPage === 'confirmNo' && (
                  <>
                    <p>{t('symptomTracking.periodReminder.laterExplanation')}</p>
                    <div>
                      <Button
                        variant="primary"
                        onClick={() => {
                          setShowPeriodReminderSuggestion(false);
                          setDidWeAlreadySuggestPeriodReminder('true');
                        }}
                      >
                        {t('symptomTracking.periodReminder.ok')}
                      </Button>
                    </div>
                  </>
                )}
                {periodReminderPage === 'confirmYes' && (
                  <>
                    <p>
                      <CheckCircleOutlined style={{ color: colors.brand.accentStrong, marginRight: 8 }} />{' '}
                      {t('symptomTracking.periodReminder.enabled')}
                    </p>
                    <div>
                      <Button
                        variant="primary"
                        onClick={() => {
                          setShowPeriodReminderSuggestion(false);
                          setDidWeAlreadySuggestPeriodReminder('true');
                        }}
                      >
                        {t('symptomTracking.periodReminder.ok')}
                      </Button>
                    </div>
                  </>
                )}
                {periodReminderPage === 'notificationsDisabled' && (
                  <>
                    <p>{t('symptomTracking.reminder.enableNotifications')}</p>
                    <div>
                      <Button
                        variant="primary"
                        onClick={() => {
                          posthog.capture('notification_suggester_clicked');
                          enableNotifications();
                        }}
                      >
                        {t('symptomTracking.periodReminder.enable')}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            }
          />
        )}

        {/* Horizontal cards */}
        <HorizontalTrackingCards
          displayTomorrow={!areDailyNotificationsEnabled}
          onOpenCalendar={() => setIsCalendarOpenWithScrollHandler(true)}
        />

        {/* Past cycles length */}
        {isPeriodTrackingEnabled && (
          <ModuleCard
            title={t('symptomTracking.modules.cycleLengthTitle')}
            topRightContent={
              <QuestionCircleOutlined
                onClick={() => {
                  setShowCycleLengthHelp(true);
                  posthog.capture('tracking.cycle_length_help_opened');
                }}
                className={styles.helperIcon}
              />
            }
          >
            <ErrorBoundary
              FallbackComponent={ErrorFallback}
              onError={(error, errorInfo) => console.error('Error in PastCyclesLength', error, errorInfo)}
            >
              <PastCyclesLength onFillCycles={onFillCycles} />
            </ErrorBoundary>
          </ModuleCard>
        )}

        {/* History / compare toggle */}
        {enoughDataForHistory && isPeriodTrackingEnabled && (
          <div className={styles.graphSwitcher}>
            <Radio.Group
              size="large"
              onChange={e => {
                setSelectedTab(e.target.value);
                posthog.capture('tracking.history_graph_switcher_changed', {
                  selectedTab: e.target.value,
                });
              }}
              value={selectedTab}
              buttonStyle="solid"
            >
              <Radio.Button value="history">
                <HistoryOutlined style={{ marginRight: 8 }} />
                {t('symptomTracking.modules.historyTitle')}
              </Radio.Button>
              <Radio.Button value="compare">
                <SwapOutlined style={{ marginRight: 8 }} />
                {t('symptomTracking.modules.perCycle')}
              </Radio.Button>
            </Radio.Group>
          </div>
        )}

        {/* History / cycles comparison module */}
        {selectedTab === 'history' ? (
          <HistoryModule enoughDataForHistory={!!enoughDataForHistory} />
        ) : (
          enoughDataForHistory &&
          isPeriodTrackingEnabled && (
            <ModuleCard
              title={t('symptomTracking.modules.compareCyclesTitle')}
              topRightContent={
                <QuestionCircleOutlined
                  onClick={() => {
                    setShowCompareCyclesHelp(true);
                    posthog.capture('tracking.compare_cycles_help_opened');
                  }}
                  className={styles.helperIcon}
                />
              }
            >
              <ErrorBoundary
                FallbackComponent={ErrorFallback}
                onError={(error, errorInfo) => console.error('Error in CompareCycles', error, errorInfo)}
              >
                <CompareCycles />
              </ErrorBoundary>
            </ModuleCard>
          )
        )}

        {/* No periods module */}
        {!isPeriodTrackingEnabled && (
          <ModuleCard title={t('symptomTracking.graphs.menstruation')}>
            <div className={styles.noPeriodsContainer}>
              <p>{t('symptomTracking.tracking.disabled')}</p>
              <Button
                variant="link"
                onClick={() => {
                  setShouldAskForPeriod(true);
                  posthog.capture('tracking.period_tracking_reactivated');
                }}
              >
                {t('symptomTracking.tracking.reactivate')}
              </Button>
            </div>
          </ModuleCard>
        )}
        {!hasTrackingForToday && <div className={styles.emptySpaceBehindSurveyButton} />}
      </div>
      {!hasTrackingForToday && (
        <div className={styles.fillSurveyButton}>
          <Button
            variant="primary"
            size="lg"
            block
            onClick={() => {
              setIsSurveyModalOpen(true);
              posthog.capture('tracking.survey.started', {
                source: 'daily_button',
              });
            }}
          >
            {t('symptomTracking.actions.fillDailySurvey')}
          </Button>
        </div>
      )}

      {/* Survey modal */}
      <SymptomTrackingSurveyModal
        open={isSurveyModalOpen}
        key={dayjs().format('YYYY-MM-DD')}
        onClose={() => {
          setIsSurveyModalOpen(false);
          refresh();
        }}
        date={dayjs()}
      />

      {/* Helper Modals */}
      <Modal
        open={showCycleLengthHelp}
        onCancel={() => setShowCycleLengthHelp(false)}
        footer={null}
        title={t('symptomTracking.modules.cycleLengthHelp.title')}
      >
        <Trans i18nKey="symptomTracking.modules.cycleLengthHelp.description" components={{ br: <br /> }} />
        <h4>{t('symptomTracking.modules.cycleLengthHelp.howToRead')}</h4>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <CycleDotGraphExample />
        </div>
        <p>
          <Trans
            i18nKey="symptomTracking.modules.cycleLengthHelp.description2"
            components={{
              redDot: (
                <span style={{ backgroundColor: colors.dataViz.veryBad }} className={styles.cycleDotGraphExample} />
              ),
              midBlueDot: (
                <span style={{ backgroundColor: palette.teal[500] }} className={styles.cycleDotGraphExample} />
              ),
              darkBlueDot: (
                <span style={{ backgroundColor: palette.teal[900] }} className={styles.cycleDotGraphExample} />
              ),
            }}
          />
        </p>
        <Alert
          icon={<WarningFilled style={{ color: colors.dataViz.bad }} />}
          showIcon
          type="warning"
          message={t('symptomTracking.cycles.fertilityWarning')}
        />
        <p>
          <Trans
            i18nKey="symptomTracking.modules.cycleLengthHelp.description3"
            components={{
              lightBlueDot: (
                <span style={{ border: `2px solid ${palette.teal[500]}` }} className={styles.cycleDotGraphExample} />
              ),
              darkBlueDot: (
                <span style={{ border: `2px solid ${palette.teal[900]}` }} className={styles.cycleDotGraphExample} />
              ),
            }}
          />
        </p>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <CycleDotGraphExample showInProgress={true} />
        </div>
        <p>
          <Trans
            i18nKey="symptomTracking.modules.cycleLengthHelp.description4"
            components={{
              todayDot: (
                <span
                  style={{
                    width: 14,
                    height: 14,
                    border: `2px solid ${palette.derived.tealMid}`,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    verticalAlign: 'middle',
                  }}
                  className={styles.cycleDotGraphExample}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: colors.text.disabled,
                    }}
                  />
                </span>
              ),
            }}
          />
        </p>
      </Modal>

      <Modal
        open={showCompareCyclesHelp}
        onCancel={() => setShowCompareCyclesHelp(false)}
        footer={null}
        title={t('symptomTracking.modules.compareCyclesHelp.title')}
      >
        <p>
          <Trans i18nKey="symptomTracking.modules.compareCyclesHelp.description" components={{ br: <br /> }} />
        </p>
      </Modal>
    </div>
  );
}
